// Hook — Notifications locales quotidiennes via @capacitor/local-notifications
//
// Source de vérité :
//   isEnabled  ← localStorage 'selah_notif_enabled' === '1'  ET  permission accordée
//   toggle ON  ← requestPermissions() → schedule notification → setEnabled
//   toggle OFF ← cancel notification → removeItem localStorage → setEnabled
//   reschedule ← reprogramme à la nouvelle heure si isEnabled
//
// Non disponible sur web (Capacitor.isNativePlatform() === false) :
//   → isPermissionDenied = false, isEnabled = false, toggle/reschedule = no-op

import { useState, useEffect, useCallback } from 'react'
import { Capacitor }          from '@capacitor/core'
import { LocalNotifications } from '@capacitor/local-notifications'
import { NOTIF_LS_KEY }       from '@/components/OnboardingFlow'
import { supabase }           from '@/lib/supabase'

// ─── Constantes ───────────────────────────────────────────────────────────────

/** ID fixe de la notification quotidienne Selah */
const DAILY_NOTIF_ID = 1001
/** ID fixe de la notification check-in du soir */
const CHECKIN_NOTIF_ID = 1002

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Convertit "HH:MM" en { hour, minute } — fallback 08:00 */
function parseTime(timeStr: string): { hour: number; minute: number } {
  const [hStr = '8', mStr = '0'] = (timeStr ?? '08:00').split(':')
  const hour   = parseInt(hStr, 10)
  const minute = parseInt(mStr, 10)
  return {
    hour:   isNaN(hour)   ? 8 : Math.min(23, Math.max(0, hour)),
    minute: isNaN(minute) ? 0 : Math.min(59, Math.max(0, minute)),
  }
}

/** Programme (ou reprogramme) la notification quotidienne à l'heure donnée */
async function scheduleDailyNotif(timeStr: string): Promise<void> {
  const { hour, minute } = parseTime(timeStr)

  // Annuler l'éventuelle notification déjà planifiée
  try {
    await LocalNotifications.cancel({ notifications: [{ id: DAILY_NOTIF_ID }] })
  } catch {
    // Aucune notification existante — ignoré
  }

  await LocalNotifications.schedule({
    notifications: [{
      id:    DAILY_NOTIF_ID,
      title: "Ton moment Selah t'attend",
      body:  'Pause. Réflexion. Croissance.',
      schedule: {
        on:             { hour, minute },
        repeats:        true,
        allowWhileIdle: true,
      },
    }],
  })
}

/** Annule la notification quotidienne Selah */
async function cancelDailyNotif(): Promise<void> {
  try {
    await LocalNotifications.cancel({ notifications: [{ id: DAILY_NOTIF_ID }] })
  } catch {
    // Ignoré si aucune notification planifiée
  }
}

// ─── Types ────────────────────────────────────────────────────────────────────

export interface NotificationState {
  /** true si la notification quotidienne est active */
  isEnabled: boolean
  /** true pendant les opérations async */
  isLoading: boolean
  /** true si la permission a été refusée dans les réglages OS */
  isPermissionDenied: boolean
  /**
   * Active ou désactive la notification quotidienne.
   * @param timeStr — heure au format "HH:MM" (ex: "08:00"), utilisée à l'activation
   */
  toggle: (timeStr?: string) => Promise<void>
  /**
   * Reprogramme la notification à une nouvelle heure (si isEnabled).
   * À appeler quand l'utilisateur modifie son heure de rendez-vous.
   */
  reschedule: (timeStr: string) => Promise<void>
  /** Programme la notification check-in du soir à l'heure donnée */
  scheduleCheckinReminder: (timeStr: string) => Promise<void>
  /** Annule la notification check-in du soir */
  cancelCheckinReminder: () => Promise<void>
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Re-schedules the daily notification at app startup if it was previously enabled.
 * iOS can silently remove local notifications after an app update — this ensures
 * the notification is always re-created when the app launches.
 *
 * Call once at app startup (e.g. in AppRoutes useEffect).
 */
export async function ensureDailyNotifAtStartup(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return

  const lsEnabled = localStorage.getItem(NOTIF_LS_KEY) === '1'
  if (!lsEnabled) return

  try {
    const { display } = await LocalNotifications.checkPermissions()
    if (display !== 'granted') return

    // Fetch the user's reminder time from Supabase profile
    const { data: { session } } = await supabase.auth.getSession()
    if (!session?.user?.id) return

    const { data: profile } = await (supabase.from('user_profile') as any)
      .select('daily_reminder_time')
      .eq('user_id', session.user.id)
      .maybeSingle()

    const timeStr = profile?.daily_reminder_time ?? '08:00'
    await scheduleDailyNotif(timeStr)
  } catch {
    // Non-blocking — notification will be rescheduled next time settings are opened
  }
}

/**
 * Re-schedules the check-in notification at app startup if enabled in preferences.
 * Same pattern as ensureDailyNotifAtStartup — iOS can silently remove local notifications.
 */
export async function ensureCheckInNotifAtStartup(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return

  // Note : pas de gate sur NOTIF_LS_KEY ici — le check-in a son propre toggle
  // (notification_preferences.checkin_reminder) indépendant du "moment Selah".
  // On vérifie uniquement la permission OS et la préférence Supabase.

  try {
    const { display } = await LocalNotifications.checkPermissions()
    if (display !== 'granted') return

    const { data: { session } } = await supabase.auth.getSession()
    if (!session?.user?.id) return

    // Check if check-in reminder is enabled in user preferences
    const { data: prefData } = await (supabase.from('notification_preferences') as any)
      .select('checkin_reminder, checkin_time')
      .eq('user_id', session.user.id)
      .maybeSingle()

    // Default: enabled at 21:00
    const checkinEnabled = prefData?.checkin_reminder ?? true
    if (!checkinEnabled) return

    const timeStr = prefData?.checkin_time ?? '21:00'
    const { hour, minute } = parseTime(timeStr)

    try {
      await LocalNotifications.cancel({ notifications: [{ id: CHECKIN_NOTIF_ID }] })
    } catch { /* ignored */ }

    await LocalNotifications.schedule({
      notifications: [{
        id: CHECKIN_NOTIF_ID,
        title: 'Check-in du soir',
        body: 'Comment s\'est passée ta journée ? Fais ton check-in',
        schedule: {
          on: { hour, minute },
          repeats: true,
          allowWhileIdle: true,
        },
        extra: { route: '/app/checkin' },
      }],
    })
  } catch {
    // Non-blocking
  }
}

export function useNotifications(): NotificationState {
  const isNative = Capacitor.isNativePlatform()

  const [isEnabled,          setIsEnabled]          = useState(false)
  const [isLoading,          setIsLoading]          = useState(true)
  const [isPermissionDenied, setIsPermissionDenied] = useState(false)

  // ─── Initialisation ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isNative) {
      // LocalNotifications non disponible sur le web
      setIsLoading(false)
      return
    }

    const init = async () => {
      try {
        const lsEnabled        = localStorage.getItem(NOTIF_LS_KEY) === '1'
        const { display }      = await LocalNotifications.checkPermissions()

        if (display === 'denied') {
          // Permission révoquée depuis les réglages OS
          setIsPermissionDenied(true)
          setIsEnabled(false)
          if (lsEnabled) {
            localStorage.removeItem(NOTIF_LS_KEY)
            await cancelDailyNotif()
          }
        } else if (display === 'granted' && lsEnabled) {
          // Notification active — état cohérent
          setIsEnabled(true)
        } else {
          // Désactivé ou permission pas encore demandée
          setIsEnabled(false)
        }
      } catch {
        setIsEnabled(false)
      } finally {
        setIsLoading(false)
      }
    }

    init()
  }, [isNative])

  // ─── toggle ─────────────────────────────────────────────────────────────────
  const toggle = useCallback(async (timeStr = '08:00') => {
    if (!isNative) return
    setIsLoading(true)

    try {
      if (isEnabled) {
        // ── Désactiver ──────────────────────────────────────────────────────
        await cancelDailyNotif()
        localStorage.removeItem(NOTIF_LS_KEY)
        setIsEnabled(false)
      } else {
        // ── Activer ─────────────────────────────────────────────────────────
        const { display } = await LocalNotifications.requestPermissions()

        if (display === 'denied') {
          setIsPermissionDenied(true)
          setIsEnabled(false)
          localStorage.removeItem(NOTIF_LS_KEY)
        } else if (display === 'granted') {
          setIsPermissionDenied(false)
          await scheduleDailyNotif(timeStr)
          localStorage.setItem(NOTIF_LS_KEY, '1')
          setIsEnabled(true)
        }
        // Dialogue fermé sans réponse → aucun changement d'état
      }
    } catch (e) {
      if (!import.meta.env.PROD) console.error('[Notifications] Erreur toggle :', e)
      // Rollback partiel : s'assurer qu'on n'est pas dans un état incohérent
      if (!isEnabled) {
        setIsEnabled(false)
        localStorage.removeItem(NOTIF_LS_KEY)
      }
    } finally {
      setIsLoading(false)
    }
  }, [isNative, isEnabled])

  // ─── reschedule ─────────────────────────────────────────────────────────────
  const reschedule = useCallback(async (timeStr: string) => {
    if (!isNative || !isEnabled) return
    try {
      await scheduleDailyNotif(timeStr)
    } catch (e) {
      if (!import.meta.env.PROD) console.error('[Notifications] Erreur reprogrammation :', e)
    }
  }, [isNative, isEnabled])

  // ─── Check-in reminder ────────────────────────────────────────────────────���
  const scheduleCheckinReminder = useCallback(async (timeStr: string) => {
    if (!isNative) return
    const { hour, minute } = parseTime(timeStr)
    try {
      await LocalNotifications.cancel({ notifications: [{ id: CHECKIN_NOTIF_ID }] })
    } catch { /* ignored */ }

    const { display } = await LocalNotifications.checkPermissions()
    if (display !== 'granted') return

    await LocalNotifications.schedule({
      notifications: [{
        id: CHECKIN_NOTIF_ID,
        title: 'Check-in du soir',
        body: 'Comment s\'est passée ta journée ? Fais ton check-in',
        schedule: {
          on: { hour, minute },
          repeats: true,
          allowWhileIdle: true,
        },
        extra: { route: '/app/checkin' },
      }],
    })
  }, [isNative])

  const cancelCheckinReminder = useCallback(async () => {
    if (!isNative) return
    try {
      await LocalNotifications.cancel({ notifications: [{ id: CHECKIN_NOTIF_ID }] })
    } catch { /* ignored */ }
  }, [isNative])

  return { isEnabled, isLoading, isPermissionDenied, toggle, reschedule, scheduleCheckinReminder, cancelCheckinReminder }
}
